import {
  HTTP_RETRY_BASE_DELAY_MS,
  HTTP_RETRY_MAX_DELAY_MS,
  HTTP_STATUS_SERVER_ERROR_FLOOR,
  HTTP_STATUS_TOO_MANY_REQUESTS,
  MS_PER_SECOND,
} from "../constants/app";
import type { IErrorEnvelope, IGetRetryDelayMsProps } from "../types/http";
import { isRecord } from "./type-guards";

// NOTE: NSAI-4624 — "may this request be sent again, and when?", as pure
// functions every client shares. Extracted from utils/skill-errors.ts, which
// had answered it for Skills alone: the chat document thumbnail needed the same
// answer, and a second copy is how the two would have drifted.
//
// The reason this matters beyond tidiness: NSAI-4472's admission gate sheds
// ordinary reads with a 503 once the server saturates, and file download /
// preview are deliberately sheddable. Every client therefore has to treat a
// 503 as "come back shortly", not as "this file is broken".

// NOTE: Reads the envelope without assuming axios. Web and mobile share one
// ApiClient today, but a caller may hand over a plain fetch rejection or a
// thrown Error, and a reader that understood only one of those would report a
// transient failure as permanent.
export const readErrorEnvelope = (error: unknown): IErrorEnvelope => {
  const empty: IErrorEnvelope = {
    statusCode: null,
    retryAfterSeconds: null,
    errorCode: null,
  };

  if (!isRecord(error)) {
    return empty;
  }

  const response = isRecord(error.response) ? error.response : null;
  const body = response && isRecord(response.data) ? response.data : {};

  const statusCode =
    response && typeof response.status === "number"
      ? response.status
      : typeof body.statusCode === "number"
        ? body.statusCode
        : null;

  // NOTE: `body.message` is deliberately never read. Reading it at all invites a
  // later edit that renders it, and a server message can quote a resource path
  // or a signed URL.
  return {
    statusCode,
    retryAfterSeconds:
      typeof body.retryAfterSeconds === "number"
        ? body.retryAfterSeconds
        : null,
    // NOTE: A stable enum code, NOT prose — the one field on an error body that
    // is safe to branch on (e.g. the gate's SERVER_OVERLOADED).
    errorCode: typeof body.errorCode === "string" ? body.errorCode : null,
  };
};

// NOTE: Retryable means "the same request may safely be sent again". A null
// status is a request that never got an answer (network drop, DNS, an aborted
// connection) and is retryable for that reason. A 409 is excluded even though
// it looks transient: retrying it unchanged would overwrite whatever moved
// underneath. Other 4xx are excluded because the input has to change first.
export const isRetryableStatus = (statusCode: number | null): boolean =>
  statusCode === null ||
  statusCode === HTTP_STATUS_TOO_MANY_REQUESTS ||
  statusCode >= HTTP_STATUS_SERVER_ERROR_FLOOR;

export const isRetryableHttpError = (error: unknown): boolean =>
  isRetryableStatus(readErrorEnvelope(error).statusCode);

// NOTE: The server's own `retryAfterSeconds` wins whenever it sent one, because
// it knows why it refused — the backpressure gate's 2s is a re-evaluated
// saturation snapshot, not a guess. It is read from the BODY rather than the
// `Retry-After` header on purpose: `enableCors` declares no `exposedHeaders`, so
// a cross-origin caller (the browser extension) cannot read that header at all,
// which is precisely why the gate duplicates the value into the body.
//
// Falls back to exponential backoff, and clamps both paths to
// HTTP_RETRY_MAX_DELAY_MS so a hostile or mistaken value cannot strand a
// spinner. A non-positive `retryAfterSeconds` is treated as absent rather than
// as "retry immediately", which would hammer a server that just said it is
// overloaded.
export const getRetryDelayMs = ({
  error,
  attempt,
}: IGetRetryDelayMsProps): number => {
  const { retryAfterSeconds } = readErrorEnvelope(error);

  if (retryAfterSeconds !== null && retryAfterSeconds > 0) {
    return Math.min(retryAfterSeconds * MS_PER_SECOND, HTTP_RETRY_MAX_DELAY_MS);
  }

  return Math.min(
    HTTP_RETRY_BASE_DELAY_MS * 2 ** Math.max(attempt, 0),
    HTTP_RETRY_MAX_DELAY_MS,
  );
};
